import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Button, Tooltip, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, Fragment, useContext, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import {
  Creation,
  CreationState,
  Creative,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  Deliverable,
  File,
  Issue,
  IssueComment,
  Maybe,
  OfferPricingDetailItem,
  Request,
  RequestItem,
  RequestState,
  User,
} from '../../../graphql/generated/graphql';
import { Currency } from '../../../graphql/generated/graphql_offer';
import theme from '../../../theme';
import { toJSDate } from '../../../utility/graphql/datetime';
import DeliverableDetail from '../../component/compound/CreationDetail/DeliverableDetail';
import _ModificationRequest from '../../component/compound/CreationDetail/ModificationRequest';
import SubmissionForm from '../../component/compound/CreationDetail/SubmissionForm';
import OfferRewardDisplay from '../../component/compound/OfferRewardDisplay';
import LfToBr from '../../component/primitive/LfToBr';
import PublicId from '../../component/primitive/PublicId';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as StateContext } from '../../context/state';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutItem } from '../../styleUtility/gridLayout';
import useCreateModificationRequestDeliverable from './useCreateModificationRequestDeliverable';
import useCreateModificationRequestIssue from './useCreateModificationRequestIssue';
import useCreateModificationRequestIssueComment from './useCreateModificationRequestIssueComment';
import useReopenModificationRequestIssue from './useReopenModificationRequestIssue';
import useResolveModificationRequestIssue from './useResolveModificationRequestIssue';
import useTouchModificationRequest from './useTouchModificationRequest';

interface Props {
  creativeFile: Pick<File, 'mimeType' | 'url'>;
  submittedDeliverable?: Pick<Deliverable, 'id'>;
  creation: Pick<Creation, 'state' | 'assignmentId'> & {
    creativeFormat: Pick<CreativeFormat, 'id'> & {
      creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
      attribute: {
        playbackTime?: {
          seconds: CreativeFormatPlaybackTime['seconds'];
        } | null;
        aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
        fastStart?: CreativeFormatFastStart | null;
      };
    };
  };
  hasDeliverables: boolean;
  modificationRequest: Pick<Request, 'id' | 'state' | 'bucketId' | 'touchedAt'> & {
    deliverables: (Pick<Deliverable, 'intent' | 'createdAt'> & {
      creative: Pick<Creative, 'id'> & {
        file: Pick<File, 'url' | 'mimeType' | 'fastStart' | 'attribute' | 'originalFileName'>;
      };
      attachedThumbnail?: Maybe<{
        file: Pick<File, 'url' | 'size' | 'originalFileName'>;
      }>;
    })[];
    offerPricingDetail?: Maybe<{
      basicPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
      optionPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
    }>;
    items: (Pick<RequestItem, 'id' | 'typeName' | 'metadata' | 'sender' | 'assetIds' | 'body'> & {
      issue?: Maybe<
        Pick<Issue, 'id' | 'resolved'> & {
          comments: (Pick<IssueComment, 'assetIds' | 'body' | 'createdAt'> & {
            user: Pick<User, 'name' | 'profileImageUrl'>;
          })[];
        }
      >;
    })[];
  };
  assets: Array<FileReference>;
  hideForm: boolean;
  refresh: () => void;
  className?: string;
}

const ModificationContentItem: FC<Props> = ({
  creativeFile,
  submittedDeliverable,
  creation,
  hasDeliverables,
  modificationRequest,
  assets,
  hideForm,
  refresh,
  className,
}) => {
  const { touch } = useTouchModificationRequest();
  const { createIssue } = useCreateModificationRequestIssue();
  const { createIssueComment } = useCreateModificationRequestIssueComment();
  const { resolveIssue } = useResolveModificationRequestIssue();
  const { reopenIssue } = useReopenModificationRequestIssue();
  useEffect(() => {
    if (modificationRequest.touchedAt == null && modificationRequest.state === 'submitted') {
      touch(modificationRequest.id, refresh);
    }
  }, [touch, refresh, modificationRequest.id, modificationRequest.touchedAt, modificationRequest.state]);

  const [isOpenRepostForm, setIsOpenRepostForm] = useState(modificationRequest.deliverables.length === 0);
  const state = useContext(StateContext);

  const { createDeliverable, creating } = useCreateModificationRequestDeliverable(modificationRequest.id);

  const deliverableSubmittedAt = (deliverable: Pick<Deliverable, 'createdAt'>) => {
    return (
      <FormattedDate
        value={toJSDate(deliverable.createdAt)}
        year="numeric"
        month="short"
        day="2-digit"
        hour="2-digit"
        minute="2-digit"
      />
    );
  };

  const submissions = (
    <Fragment>
      {modificationRequest.deliverables.map((deliverable, index) => (
        <ModificationDeliverable key={`modificationRequestHeadingDeliverable-${index}`}>
          <Heading>
            <RedeliveryHeading>
              <FormattedMessage id="modification_request.heading_deliverable" />（{deliverableSubmittedAt(deliverable)}
              ）
              <DeliverableId>
                {submittedDeliverable ? (
                  <PublicId id={submittedDeliverable.id} variant="caption" color="textSecondary" />
                ) : null}
              </DeliverableId>
            </RedeliveryHeading>
          </Heading>
          <DeliverableDetail
            creativeFormat={creation.creativeFormat}
            deliverable={deliverable}
            type="modification"
            className={className}
          />
        </ModificationDeliverable>
      ))}
      {hideForm ? (
        <Typography variant="caption">
          <FormattedMessage id="creation_state.canceled" />
        </Typography>
      ) : modificationRequest.state === RequestState.GivenFeedback ? (
        <Typography color="error">
          <FormattedMessage id="modification_request.state.given_feedback" />
        </Typography>
      ) : modificationRequest.state === RequestState.Approved ? (
        <Typography variant="subtitle1">
          <FormattedMessage id="modification_request.state.approved" />
        </Typography>
      ) : isOpenRepostForm ? (
        <ModificationDeliverable>
          <TitleBar>
            <Heading>
              <FormattedMessage id="modification_request.heading_deliverable" />
            </Heading>
            {state.language == 'ja-JP' && (
              <FormattedMessage id="submission.help">
                {(text: string) => (
                  <Tooltip placement="top" title={text}>
                    <HelpAnchor
                      href="https://help.kaizen-ad.com/ja/articles/5590559-納品物投稿時の対応ファイル種別"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Help />
                    </HelpAnchor>
                  </Tooltip>
                )}
              </FormattedMessage>
            )}
          </TitleBar>
          <SubmissionForm
            creativeFormat={creation.creativeFormat}
            canceled={creation.state !== CreationState.Canceled}
            hasDeliverables={hasDeliverables}
            submit={createDeliverable}
            submitting={creating}
            type="modification"
            onSubmit={refresh}
          />
        </ModificationDeliverable>
      ) : (
        <ModificationDeliverable>
          <Typography variant="subtitle1">
            <FormattedMessage id="modification_request.submit.message">
              {(text: string) => <LfToBr text={text} />}
            </FormattedMessage>
          </Typography>
          <RedeliveryButtonWrapper>
            <Button color="primary" variant="contained" onClick={() => setIsOpenRepostForm(true)}>
              <FormattedMessage id="modification_request.repost.button.label" />
            </Button>
          </RedeliveryButtonWrapper>
        </ModificationDeliverable>
      )}
    </Fragment>
  );

  const [basicPricings, optionPricings] = (() => {
    if (modificationRequest.offerPricingDetail) {
      return [
        modificationRequest.offerPricingDetail.basicPricings,
        modificationRequest.offerPricingDetail.optionPricings,
      ];
    } else {
      return [[], []];
    }
  })();

  return (
    <div className={className}>
      {basicPricings.length > 0 ? (
        <Fee>
          <Heading>
            <FormattedMessage id="modification_request.heading_fee" />
          </Heading>
          <RewardContent>
            <ModificationRequestReward
              offer={{
                rewardCurrency: basicPricings[0].rewardCurrency as Currency,
                rewardAmount:
                  basicPricings.reduce((accum, basicPricing) => {
                    return accum + basicPricing.rewardAmount;
                  }, 0) +
                  optionPricings.reduce((accum, optionPricing) => {
                    return accum + optionPricing.rewardAmount;
                  }, 0),
              }}
              isAvailable={true}
            />
          </RewardContent>
        </Fee>
      ) : null}

      <ModificationRequestItems
        creativeFile={creativeFile}
        hasSubmittedDeliverable={!!submittedDeliverable}
        modificationRequest={modificationRequest}
        assets={assets}
        onRequestCreateModificationRequestIssue={(id: string, body: string) => {
          createIssue(id, body, refresh);
        }}
        onRequestCreateModificationRequestItemIssueComment={(
          id: string,
          body: string,
          bucketId: string,
          assetIds: string[],
        ) => {
          createIssueComment(id, body, bucketId, assetIds, refresh);
        }}
        onRequestResolveModificationRequestIssue={(id: string) => {
          resolveIssue(id, refresh);
        }}
        onRequestReopenModificationRequestIssue={(id: string) => {
          reopenIssue(id, refresh);
        }}
      />

      {submissions}
    </div>
  );
};

const UpdateDateTime = styled(Typography)({ marginLeft: SPACING_REGULAR });
UpdateDateTime.defaultProps = { variant: 'caption' };

const Fee = styled('div')({
  marginBottom: SPACING_UNIT * 3,
});

const Heading = styled(Typography)({ display: 'inline-block' });
Heading.defaultProps = { variant: 'subtitle1' };

const TitleBar = styled('div')({
  ...flexibleLayoutContainer({ alignItems: 'center' }),
});

const RedeliveryHeading = styled('div')({
  ...flexibleLayoutContainer({ alignItems: 'center' }),
  marginBottom: SPACING_REGULAR,
});

const DeliverableId = styled(Typography)({ marginLeft: SPACING_REGULAR });

const ModificationRequestItems = styled(_ModificationRequest)({
  marginBottom: SPACING_UNIT * 5,
});

const ModificationRequestReward = styled(OfferRewardDisplay)({
  ...gridLayoutItem('reward'),
  display: 'inline-block',
  width: 152,
  textAlign: 'end',
});

const RewardContent = styled('div')({
  display: 'inline-block',
  paddingLeft: SPACING_REGULAR,
  paddingRight: SPACING_REGULAR,
});

const ModificationDeliverable = styled('div')({
  paddingBottom: SPACING_REGULAR,
  paddingTop: SPACING_REGULAR,
  borderTop: `1px solid ${theme.palette.grey['200']}`,
});

const RedeliveryButtonWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
});

const HelpAnchor = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.grey['500'],
  textDecoration: 'none',
  marginLeft: SPACING_REGULAR,
});

export default ModificationContentItem;
